import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { CategoryEntity, SubCategoryEntity, BrandEntity } from 'sl-api-connector';

type CategoryOption = CategoryEntity & {
  isSelected: boolean;
};

type SubcategoryOption = SubCategoryEntity & {
  isSelected: boolean;
};

type BrandOption = Partial<BrandEntity> & {
  id: string | number;
  displayName: string;
  isSelected: boolean;
  brandId?: number;
};

type SegmentOption = {
  id: string;
  displayName: string;
  isSelected: boolean;
};

interface AddProductsModalFilters {
  categories: CategoryOption[];
  subcategories: SubcategoryOption[];
  brands: BrandOption[];
  segments: SegmentOption[];
}

interface AddProductsModalContextValue {
  filters: AddProductsModalFilters;
  setCategories: (categories: CategoryOption[]) => void;
  setSubcategories: (subcategories: SubcategoryOption[]) => void;
  setBrands: (brands: BrandOption[]) => void;
  setSegments: (segments: SegmentOption[]) => void;
  setSelectedBusinessUnitIds: (ids: string[]) => void;
  clearFilters: () => void;
  selectedCategoryIds: (string | number)[];
  selectedSubcategoryIds: (string | number)[];
  selectedBrandIds: (string | number)[];
  selectedSegmentId?: string;
  selectedBusinessUnitIds: string[];
  // Provides filters in AppFilters format to override the global AppFilter state
  getOverrideAppFilters: () => {
    categoryFilters: (string | number)[] | null;
    subcategoryFilters: (string | number)[] | null;
    segmentFilters?: { id: string } | null;
    businessUnitFilters?: string[] | null;
  } | null;
}

const AddProductsModalContext = createContext<AddProductsModalContextValue | undefined>(undefined);

interface AddProductsModalProviderProps {
  children: React.ReactNode;
  initialCategories: CategoryOption[];
  initialSubcategories: SubcategoryOption[];
  initialBrands: BrandOption[];
  initialSegments: SegmentOption[];
}

export const AddProductsModalProvider: React.FC<AddProductsModalProviderProps> = ({
  children,
  initialCategories,
  initialSubcategories,
  initialBrands,
  initialSegments
}) => {
  const [categories, setCategories] = useState<CategoryOption[]>(initialCategories);
  const [subcategories, setSubcategories] = useState<SubcategoryOption[]>(initialSubcategories);
  const [brands, setBrands] = useState<BrandOption[]>(initialBrands);
  const [segments, setSegments] = useState<SegmentOption[]>(initialSegments);
  const [selectedBusinessUnitIds, setSelectedBusinessUnitIds] = useState<string[]>([]);

  const selectedCategoryIds = useMemo(() => {
    return categories.filter(cat => cat.isSelected).map(cat => cat.id);
  }, [categories]);

  const selectedSubcategoryIds = useMemo(() => {
    return subcategories.filter(sub => sub.isSelected).map(sub => sub.id);
  }, [subcategories]);

  const selectedBrandIds = useMemo(() => {
    return brands.filter(b => b.isSelected).map(b => b.id);
  }, [brands]);

  const selectedSegmentId = useMemo(() => {
    const sel = segments.find(s => s.isSelected);
    return sel?.id;
  }, [segments]);

  const clearFilters = useCallback(() => {
    setCategories(prev => prev.map(cat => ({ ...cat, isSelected: false })));
    setSubcategories(prev => prev.map(sub => ({ ...sub, isSelected: false })));
    setBrands(prev => prev.map(b => ({ ...b, isSelected: false })));
    setSegments(prev => prev.map(s => ({ ...s, isSelected: false })));
    setSelectedBusinessUnitIds([]);
  }, []);

  const getOverrideAppFilters = useCallback(() => {
    // If no filters are selected, return null to use global AppFilters
    if (
      selectedCategoryIds.length === 0 &&
      selectedSubcategoryIds.length === 0 &&
      selectedBrandIds.length === 0 &&
      !selectedSegmentId &&
      selectedBusinessUnitIds.length === 0
    ) {
      return null;
    }

    // Return filters in the same format as AppFilters
    return {
      categoryFilters: selectedCategoryIds.length > 0 ? selectedCategoryIds : null,
      subcategoryFilters: selectedSubcategoryIds.length > 0 ? selectedSubcategoryIds : null,
      ...(selectedSegmentId ? { segmentFilters: { id: selectedSegmentId } } : {}),
      ...(selectedBusinessUnitIds.length > 0 ? { businessUnitFilters: selectedBusinessUnitIds } : {})
    };
  }, [selectedCategoryIds, selectedSubcategoryIds, selectedBrandIds, selectedSegmentId, selectedBusinessUnitIds]);

  const value: AddProductsModalContextValue = {
    filters: {
      categories,
      subcategories,
      brands,
      segments
    },
    setCategories,
    setSubcategories,
    setBrands,
    setSegments,
    setSelectedBusinessUnitIds,
    clearFilters,
    selectedCategoryIds,
    selectedSubcategoryIds,
    selectedBrandIds,
    selectedSegmentId,
    selectedBusinessUnitIds,
    getOverrideAppFilters
  };

  return (
    <AddProductsModalContext.Provider value={value}>
      {children}
    </AddProductsModalContext.Provider>
  );
};

export const useAddProductsModalFilters = () => {
  const context = useContext(AddProductsModalContext);
  if (context === undefined) {
    throw new Error('useAddProductsModalFilters must be used within AddProductsModalProvider');
  }
  return context;
};

